
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, Loader2, CheckCircle2, Anchor, Ship, Navigation, Globe } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useInventaire } from '@/hooks/useInventaire';

interface Division240SelectorProps {
  selectedBateauId: string;
  onEquipmentAdded: () => void;
}

const CATEGORIES_NAVIGATION = [
  {
    id: 'basique',
    label: 'Basique',
    description: "Jusqu'à 2 milles",
    icon: Anchor,
    color: 'bg-green-100 text-green-700',
  },
  {
    id: 'cotier',
    label: 'Côtier',
    description: "Jusqu'à 6 milles",
    icon: Ship,
    color: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'semi-hauturier',
    label: 'Semi-hauturier',
    description: '6 à 60 milles',
    icon: Navigation,
    color: 'bg-orange-100 text-orange-700',
  },
  {
    id: 'hauturier',
    label: 'Hauturier',
    description: '> 60 milles',
    icon: Globe,
    color: 'bg-red-100 text-red-700',
  },
];

const EQUIPEMENTS_BASIQUE = [
  'Équipement individuel de flottabilité',
  'Dispositif lumineux',
  'Moyens mobiles de lutte contre l\'incendie',
  'Dispositif d\'assèchement manuel',
  'Dispositif de remorquage',
  'Ligne de mouillage',
  'Annuaire des marées',
  'Dispositif de repérage et d\'assistance personne à la mer (bouée)',
];

const EQUIPEMENTS_COTIER = [
  '3 feux rouges à main',
  'Compas magnétique (ou GPS)',
  'Cartes marines officielles',
];

const EQUIPEMENTS_SEMI_HAUTURIER = [
  'Pavillon national',
  'Règlement RIPAM',
  'Description du système de balisage',
  'Livre des feux tenu à jour',
  'Harnais et longe',
  'VHF fixe',
];

const EQUIPEMENTS_HAUTURIER = [
  'Radeau de survie',
  'Matériel pour faire le point',
  'Journal de bord',
  'Dispositif de réception météo',
  'Trousse de secours',
  'Dispositif lumineux recherche de nuit',
  'Radiobalise EPIRB',
  'VHF fixe + VHF portative',
];

const getEquipementsPourCategorie = (categorieId: string): string[] => {
  switch (categorieId) {
    case 'basique':
      return [...EQUIPEMENTS_BASIQUE];
    case 'cotier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER];
    case 'semi-hauturier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER];
    case 'hauturier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER, ...EQUIPEMENTS_HAUTURIER];
    default:
      return [];
  }
};

const Division240Selector = ({ selectedBateauId, onEquipmentAdded }: Division240SelectorProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { ajouterPlusieursArticles, articles } = useInventaire();

  const equipements = selectedCategory ? getEquipementsPourCategorie(selectedCategory) : [];

  const handleAjouterEquipements = async () => {
    if (!selectedCategory || !selectedBateauId) return;

    setLoading(true);
    try {
      // Filter out equipment that already exists in inventory for this boat
      const articlesExistants = articles
        .filter(a => a.bateau_id === selectedBateauId && a.categorie === 'Sécurité')
        .map(a => a.nom.toLowerCase());

      const nouveauxEquipements = equipements.filter(
        eq => !articlesExistants.includes(eq.toLowerCase())
      );

      if (nouveauxEquipements.length === 0) {
        toast({
          title: 'Déjà à jour',
          description: 'Tous les équipements de cette catégorie sont déjà dans votre inventaire.',
        });
        setLoading(false);
        return;
      }

      const getQuantiteMax = (nom: string): number => {
        const nomLower = nom.toLowerCase();
        if (nomLower.includes('équipement individuel de flottabilité')) return 8;
        if (nomLower.includes('feux rouges à main')) return 3;
        return 1;
      };

      const articlesData = nouveauxEquipements.map(nom => ({
        nom,
        categorie: 'Sécurité',
        sous_categorie: `Division 240 - ${CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.label}`,
        quantite: 0,
        quantite_max: getQuantiteMax(nom),
        unite: 'unité',
        description: `Équipement obligatoire Division 240 - Navigation ${CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.label}`,
        notes: null,
        prix_unitaire: null,
        date_achat: null,
        date_peremption: null,
        espace_stockage_id: null,
        bateau_id: selectedBateauId,
      }));

      const result = await ajouterPlusieursArticles(articlesData, selectedBateauId);

      if (result.error) {
        toast({
          title: 'Erreur',
          description: "Impossible d'ajouter les équipements.",
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Équipements ajoutés',
          description: `${nouveauxEquipements.length} équipement(s) de sécurité ajouté(s) à votre inventaire.`,
        });
        onEquipmentAdded();
      }
    } catch (err) {
      console.error('Erreur:', err);
      toast({
        title: 'Erreur',
        description: 'Une erreur inattendue s\'est produite.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-nautic">
          <Shield className="h-5 w-5" />
          Division 240 — Matériel de sécurité obligatoire
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Sélectionnez votre catégorie de navigation pour ajouter automatiquement le matériel de sécurité requis.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioGroup value={selectedCategory} onValueChange={setSelectedCategory} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CATEGORIES_NAVIGATION.map(cat => {
            const Icon = cat.icon;
            return (
              <Label
                key={cat.id}
                htmlFor={`nav-${cat.id}`}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedCategory === cat.id
                    ? 'border-nautic bg-nautic/5'
                    : 'border-border hover:border-nautic/40'
                }`}
              >
                <RadioGroupItem value={cat.id} id={`nav-${cat.id}`} />
                <div className={`p-2 rounded-lg ${cat.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">{cat.label}</p>
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                </div>
              </Label>
            );
          })}
        </RadioGroup>

        {selectedCategory && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-nautic">
                Équipements requis ({equipements.length})
              </h4>
              <Badge variant="outline" className="text-xs">
                {CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.label}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {equipements.map((eq, i) => {
                const dejaPresent = articles.some(
                  a => a.bateau_id === selectedBateauId && a.nom.toLowerCase() === eq.toLowerCase() && a.categorie === 'Sécurité'
                );
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-md text-sm ${
                      dejaPresent ? 'bg-green-50 text-green-700' : 'bg-muted/50'
                    }`}
                  >
                    {dejaPresent ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span>{eq}</span>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleAjouterEquipements}
              disabled={loading}
              className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Ajouter les équipements manquants à l'inventaire
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Division240Selector;
